import React from 'react';
import {
  TextField,
  MenuItem,
  Box,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText,
  FormGroup,
  Button
} from '@mui/material';
import {
  Person,
  School,
  FamilyRestroom,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  HealthAndSafety,
  Church,
  Info,
  Add,
  Remove
} from '@mui/icons-material';

const iconMap = {
  Person,
  School,
  FamilyRestroom,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  HealthAndSafety,
  Church,
  Info,
  Add,
  Remove
};

const FormField = ({ field, value, onChange, onButtonClick, error }) => {
  const {
    name,
    label,
    type,
    gridSize,
    icon,
    placeholder,
    rows,
    options,
    helperText,
    sx: customSx
  } = field;

  const IconComponent = icon ? iconMap[icon] : null;

  const getInputProps = () => {
    const props = {};

    if (type === 'date') {
      props.InputLabelProps = { shrink: true };
    }

    if (IconComponent) {
      props.InputProps = {
        startAdornment: (
          <InputAdornment
            position="start"
            sx={type === 'textarea' ? { alignSelf: 'flex-start', mt: 1 } : {}}
          >
            <IconComponent color="action" />
          </InputAdornment>
        ),
      };
    }

    return props;
  };

  const renderField = () => {
    const commonProps = {
      fullWidth: true,
      label,
      name,
      value,
      onChange,
      error: !!error,
      helperText: error || helperText,
      variant: "outlined",
      placeholder,
      sx: { 
        '& .MuiOutlinedInput-root': { borderRadius: 2 },
        '& .MuiInputLabel-root': {
          fontSize: '1rem',
          fontWeight: 500,
          color: 'rgba(0, 0, 0, 0.7)',
          overflow: 'visible',
          whiteSpace: 'nowrap',
        },
        '& .MuiInputBase-root': {
          width: '100%',
          minHeight: '56px',
        },
        ...(customSx || {})
      },
      ...getInputProps()
    };

    switch (type) {
      case 'select':
        return (
          <TextField 
            {...commonProps} 
            select
            sx={{ 
              ...commonProps.sx,
              width: '100%', // Ensure dropdown takes full width
              '& .MuiSelect-select': {
                minHeight: '56px',
                display: 'flex',
                alignItems: 'center',
                padding: '12px 14px',
              }
            }}
          >
            {options?.map((option) => (
              <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );

      case 'textarea':
        return (
          <TextField
            {...commonProps}
            multiline
            rows={rows || 4}
            sx={{
              ...commonProps.sx,
              width: '100%',
              '& .MuiInputBase-root': {
                width: '100%',
                minHeight: '120px', // Taller for textarea
                padding: '12px 14px',
              },
              '& .MuiInputLabel-root': {
                background: 'grey.50',
                padding: '0 8px',
                margin: '-6px 0 0 -6px',
                borderRadius: '4px',
              }
            }}
          />
        );

      case 'radio':
        return (
          <FormControl component="fieldset" error={!!error} fullWidth sx={{ minHeight: '56px' }}>
            <FormLabel component="legend" sx={{ 
              fontSize: '1rem',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.7)',
              mb: 1
            }}>{label}</FormLabel>
            <RadioGroup
              name={name}
              value={value || ''}
              onChange={onChange}
              row
              sx={{ 
                '& .MuiFormControlLabel-root': {
                  marginRight: 3
                }
              }}
            >
              {options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                  label={option.label}
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.95rem',
                      fontWeight: 400
                    }
                  }}
                />
              ))}
            </RadioGroup>
            {(error || helperText) && (
              <FormHelperText sx={{ mt: 0.5 }}>{error || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'checkbox':
        return (
          <FormControl component="fieldset" error={!!error} fullWidth sx={{ minHeight: '56px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={onChange}
                  name={name}
                  sx={{ 
                    padding: '4px',
                    '& .MuiSvgIcon-root': { fontSize: 24 }
                  }}
                />
              }
              label={label}
              sx={{ 
                alignItems: 'center',
                minHeight: '48px',
                '.MuiFormControlLabel-label': { 
                  mt: 0,
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.7)',
                }
              }}
            />
            {(error || helperText) && (
              <FormHelperText sx={{ mt: 0.5 }}>{error || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'button':
        const ButtonIcon = icon ? iconMap[icon] : null;
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onButtonClick && onButtonClick(name)}
            startIcon={ButtonIcon && <ButtonIcon />}
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              px: 3,
              fontSize: '1rem',
              fontWeight: 500,
              minHeight: '48px',
              minWidth: '180px',
              boxShadow: 2
            }}
          >
            {label}
          </Button>
        );

      // This is a placeholder for the box type
      // The actual implementation will be in Application.js
      case 'box':
        return (
          <Box
            sx={{
              width: '100%',
              ...(field.sx || {})
            }}
          >
            {field.label && (
              <FormLabel
                component="legend"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.7)',
                  mb: 2,
                  display: 'block'
                }}
              >
                {field.label}
              </FormLabel>
            )}
            <Box sx={{ p: 2 }}>
              {/* Children will be rendered by Application.js */}
              {/* This is just a placeholder */}
              <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Box content will be rendered by parent component
              </Box>
            </Box>
          </Box>
        );

      default:
        return (
          <TextField 
            {...commonProps} 
            type={type} 
            sx={{
              ...commonProps.sx,
              width: '100%',
              '& .MuiInputBase-input': {
                padding: '12px 14px',
                height: '1.4375em',
              }
            }}
          />
        );
    }
  };

  // For textarea fields with span > 1, ensure they take up full width
  const isFullWidthTextarea = type === 'textarea' && field.layout?.span > 1;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {renderField()}
    </Box>
  );
};

export default FormField;
